import { FunctionComponent, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";

import { classNames } from "@/utils/classNames";

import { getStrengthDisplay } from "./runCardUtils";

/** Canonical strength options of the quick-change menu (D1), strongest first. */
export const STRENGTH_OPTIONS = [
	{ key: "s1", priority: "Prio 1" },
	{ key: "s2", priority: "Prio 2" },
	{ key: "s3", priority: "Prio 3" },
	{ key: "dq", priority: "Disqualified" },
] as const;

const MENU_WIDTH = 210;
/** Grace period for the pointer to travel from the pill into the portaled menu. */
const CLOSE_DELAY_MS = 150;

interface StrengthMenuProps {
	signalId: number;
	priority: string | null;
	onSelect: (priority: string) => void;
}

/**
 * The S1/S2/S3/DQ pill with its hover menu (mockup `changeStrength`). The menu
 * portals to `document.body` (web rule §11 — the card clips overflow), so hover
 * state is shared between the pill and the menu with a short close delay.
 */
export const StrengthMenu: FunctionComponent<StrengthMenuProps> = ({
	signalId,
	priority,
	onSelect,
}) => {
	const { t } = useTranslation();
	const pillRef = useRef<HTMLSpanElement>(null);
	const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const [isOpen, setIsOpen] = useState(false);
	const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
	const strength = getStrengthDisplay(priority);

	const open = () => {
		if (closeTimerRef.current) {
			clearTimeout(closeTimerRef.current);
			closeTimerRef.current = null;
		}

		setIsOpen(true);
	};

	const scheduleClose = () => {
		if (closeTimerRef.current) {
			clearTimeout(closeTimerRef.current);
		}

		closeTimerRef.current = setTimeout(() => setIsOpen(false), CLOSE_DELAY_MS);
	};

	useLayoutEffect(() => {
		if (!isOpen || !pillRef.current) {
			return;
		}

		const rect = pillRef.current.getBoundingClientRect();

		setPosition({
			top: rect.bottom + 4,
			left: Math.max(8, Math.min(rect.left, window.innerWidth - MENU_WIDTH - 8)),
		});
	}, [isOpen]);

	return (
		<span
			className="relative inline-flex shrink-0"
			data-feed-noselect="true"
			onMouseEnter={open}
			onMouseLeave={scheduleClose}
		>
			<span
				ref={pillRef}
				className={classNames(
					"grid h-5 w-[30px] cursor-pointer place-items-center rounded-md border-[1.5px] bg-white pb-px text-[10px] font-bold leading-none",
					strength.className
				)}
				data-testid={`feed-signal-strength-${signalId}`}
				title={t("feed.runCard.strength.menuTitle")}
			>
				{strength.label}
			</span>

			{isOpen &&
				position &&
				ReactDOM.createPortal(
					<div
						className="fixed z-[2000] flex flex-col rounded-lg border border-border-light bg-white p-1.5 shadow-lg"
						data-feed-noselect="true"
						data-testid={`feed-strength-menu-${signalId}`}
						style={{ top: position.top, left: position.left, width: MENU_WIDTH }}
						onClick={(event) => event.stopPropagation()}
						onMouseEnter={open}
						onMouseLeave={scheduleClose}
					>
						<span className="px-1.5 pb-1 pt-0.5 text-[10px] font-bold uppercase tracking-wide text-text-tertiary">
							{t("feed.runCard.strength.menuTitle")}
						</span>
						{STRENGTH_OPTIONS.map((option) => {
							const display = getStrengthDisplay(option.priority);
							const isCurrent = display.label === strength.label;

							return (
								<button
									key={option.key}
									className={classNames(
										"flex cursor-pointer items-center gap-2 rounded-md px-1.5 py-1 text-left hover:bg-background-light",
										isCurrent ? "bg-light-blue" : ""
									)}
									data-testid={`feed-strength-option-${signalId}-${option.key}`}
									type="button"
									onClick={() => {
										setIsOpen(false);
										onSelect(option.priority);
									}}
								>
									<span
										className={classNames(
											"grid h-[19px] w-7 shrink-0 place-items-center rounded border-[1.5px] bg-white pb-px text-[10px] font-bold leading-none",
											display.className
										)}
									>
										{display.label}
									</span>
									<span className="text-xs text-text">
										{t(`feed.runCard.strength.${option.key}`)}
									</span>
								</button>
							);
						})}
						<span className="mt-1 border-t border-border-light px-1.5 pb-0.5 pt-1 text-[10px] text-text-tertiary">
							{t("feed.runCard.strength.menuFooter")}
						</span>
					</div>,
					document.body
				)}
		</span>
	);
};
